import { useState } from 'react';
import { acknowledge } from '../lib/api.ts';

interface Props {
  readonly messageId: string;
  readonly onDone: () => void;
  readonly onCancel: () => void;
}

/**
 * PIN entry for an ack-required notice.
 *
 * The device proves which screen; the PIN proves which person. Without
 * this the receipt reads "opened on Reception 1", which is true but
 * nameless -- and a compliance notice needs a person to have signed
 * for it, not a screen.
 */
export const AckPrompt = ({ messageId, onDone, onCancel }: Props) => {
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    setBusy(true);
    setError('');

    const r = await acknowledge(messageId, pin);

    if (r.ok) {
      onDone();
      return;
    }

    // The server's reason, not a generic failure: "locked out" and
    // "wrong PIN, 3 left" need different things from the person.
    setError(
      r.reason === 'locked_out'
        ? `Too many attempts. Try again in ${Math.ceil(
            (r.retryAfter ?? 300) / 60,
          )} minutes.`
        : `Not a valid PIN. ${r.remaining ?? 0} attempts left.`,
    );
    setPin('');
    setBusy(false);
  };

  return (
    <div className="ackPrompt">
      <p className="mono">Enter your staff PIN to sign for this</p>

      {error && <p className="err">{error}</p>}

      <div className="noticeRow">
        <input
          className="pinInput"
          type="password"
          inputMode="numeric"
          autoFocus
          maxLength={6}
          value={pin}
          placeholder="0000"
          onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && pin.length >= 4) void submit();
          }}
        />
        <button
          className="primary"
          disabled={busy || pin.length < 4}
          onClick={() => void submit()}
        >
          {busy ? 'Checking...' : 'Acknowledge'}
        </button>
        <button onClick={onCancel}>Cancel</button>
      </div>

      <p className="hint">
        This puts your name on the record. Ask an administrator if you
        do not have a PIN.
      </p>
    </div>
  );
};
